import React from "react";
import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import moment from "moment";
import { PendingDragConfirm } from "../hooks/useDragReschedule";

interface DragConfirmModalProps {
  pendingDragConfirm: PendingDragConfirm | null;
  isConfirmingDrag: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Drag/resize confirmation — guards against fat-finger reschedules. */
const DragConfirmModal: React.FC<DragConfirmModalProps> = ({
  pendingDragConfirm,
  isConfirmingDrag,
  onConfirm,
  onCancel,
}) => (
  <Modal
    opened={pendingDragConfirm !== null}
    onClose={onCancel}
    title={
      pendingDragConfirm?.action === "resize"
        ? "Resize appointment?"
        : "Move appointment?"
    }
    size="sm"
    centered
    closeOnClickOutside={!isConfirmingDrag}
    closeOnEscape={!isConfirmingDrag}
  >
    {pendingDragConfirm && (
      <Stack gap="md">
        <Text size="sm">
          <Text span fw={600}>
            {pendingDragConfirm.title}
          </Text>{" "}
          will be updated to{" "}
          <Text span fw={600}>
            {/* newStart/newEnd are RBC "fake-local" Dates whose browser-local
                wall-clock already equals the salon's wall-clock (see
                mapTimeBlocksToEvent). Format the local components directly — a
                .tz(salonTz) here would re-shift the instant and mis-display the
                time for any operator whose browser tz differs from the salon's. */}
            {moment(pendingDragConfirm.newStart).format("ddd, MMM D · h:mm A")}
            {" – "}
            {moment(pendingDragConfirm.newEnd).format("h:mm A")}
          </Text>
          .
        </Text>

        {pendingDragConfirm.firesSms && (
          <Alert
            color="burntOrange"
            icon={<IconAlertCircle size={16} />}
            variant="light"
            p="xs"
          >
            <Text size="sm">
              The client will be texted the new appointment time.
            </Text>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            onClick={onCancel}
            disabled={isConfirmingDrag}
          >
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={onConfirm}
            loading={isConfirmingDrag}
          >
            Confirm
          </Button>
        </Group>
      </Stack>
    )}
  </Modal>
);

export default DragConfirmModal;
